<template class="sla-gauge">
  <v-row
    class="fill-height"
    no-gutters
  >
    <v-dialog
      v-model="filterDialog"
      width="50%"
      overlay-color="var(--ds-color-reverse)"
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>
            {{ filterTitleLabel }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items class="sla-gauge__close-icon">
            <span>
              <StIcon
                class="close-icon"
                icon-name="close"
                @click="filterDialog = false"
              />
            </span>
          </v-toolbar-items>
        </v-toolbar>

        <v-row
          no-gutters
          class="mt-5 mx-5"
        >
          <MultiSelectFilter
            v-model="selectedPages"
            class="mr-3"
            :select-items="getOptions"
            :label="pages"
            :searchable="true"
          />
        </v-row>

        <v-row
          no-gutters
          class="pb-5 px-5"
        >
          <v-btn
            v-if="hasFilter"
            outlined
            rounded
            class="mr-3"
            @click="clearFilter"
          >
            {{ $t('components.OperatorsLogs.trashLabel') }}
          </v-btn>
          <v-btn
            outlined
            rounded
            @click="applyFilter"
          >
            {{ $t('components.OperatorsLogs.filterLabel') }}
          </v-btn>
        </v-row>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="sla-gauge__close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="sla-gauge__modal-style">
          <h3>{{ this.$t("components.OperatorsLogs.question.title") }}</h3>
          <p>{{ this.$t("components.OperatorsLogs.question.description") }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <PercentGauge
      :score="score"
      :item-id="itemId"
      :title="title"
      :request-status="requestStatus"
      :has-custom-filter="true"
      :show-description="true"
      :open-filter="openFilter"
      :open-dialog="openDialog"
      class="make-a-copy"
      v-bind="$attrs"
      @reload="getData"
    />
  </v-row>
</template>

<script>

import { mapActions, mapGetters, mapState } from 'vuex';
import sentimentModel from '@/mixins/sentiment-model';
import EventBus from '@/utils/event-bus';
import MultiSelectFilter from '@/components/filter/MultiSelectFilter.vue';
import cardsModel from '@/mixins/cards-model';
import { DsButton } from '@ds/components';
import PercentGauge from '@/components/cards/models/PercentGauge';

export default {
  name: 'SLAGaugeChart',
  components: {
    PercentGauge,
    DsButton,
    MultiSelectFilter,
  },
  mixins: [sentimentModel, cardsModel],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      filterDialog: false,
      dialog: false,
      filter: false,
      hasFilter: false,
      selectedPages: null,
      pages: this.$t('components.OperatorsLogs.pagesName'),
      closeLabel: this.$t('components.OperatorsLogs.closeLabel'),
      filterLabel: this.$t('components.OperatorsLogs.filterLabel'),
      trashLabel: this.$t('components.OperatorsLogs.trashLabel'),
      noScoreLabel: this.$t('components.OperatorsLogs.noSLAScoreLabel'),
      score: { value: 0 },
      requestStatus: 'loading',
      noScore: false,
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
      'activeDashboard',
    ]),
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    getOptions() {
      return this.activeDashboard?.data?.pages.map((res) => res.username) || [];
    },
    filterTitleLabel() {
      return `Filtro - ${this.title}`;
    },
    ...mapState('dashboard', [
      'isStaging',
      'apiToken',
    ]),
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', ['getOperatorsLogsService']),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      const pages = this.selectedPages?.value
        ? this.selectedPages.value.map((res) => {
          const selectPage = this.activeDashboard.data.pages.find(
            (element) => element.username === res
          );
          if (selectPage) {
            return selectPage.page_id;
          }
          return null;
        })
        : null;
      this.getOperatorsLogsService({
        itemId: this.itemId,
        servicePath: 'getSLA',
        serviceParams: {
          page_ids: this.selectedPages ? pages.join(',') : null,
          api_token: this.apiToken,
        },
      }).then((res) => {
        this.noScore = Boolean(!res?.sla_score);
        this.score = { value: res.sla_score * 100 };
        this.requestStatus = 'success';
      }).catch(error => {
        console.error(error);
        this.requestStatus = 'error';
      });

      if (options?.silentRefresh) {
        EventBus.$emit('updated', { itemId: this.itemId });
      }
    },
    applyFilter(options) {
      this.hasFilter = true;
      this.getData(options);
    },
    openFilter() {
      this.filterDialog = true;
    },
    clearFilter() {
      this.selectedPages = null;
      this.hasFilter = false;
    },
    openDialog() {
      this.dialog = true;
    },
    resetFilter() {
      this.selectedPages = null;
      this.hasFilter = false;
      this.getData();
    },
  },
};
</script>

<style lang="scss" scoped>
.sla-gauge {
  &__internal-style {
    width: 100%;
    padding: 1rem;
    display: flex;
  }

  &__close-icon {
    align-items: center;
  }

  &__buttons {
    display: flex;
  }

  &__button {
    box-shadow: none;
    border-radius: unset;
  }

  &__close-button {
    display: flex;
    flex: end;
    justify-content: flex-end;
  }
}

::v-deep {
  .sla-gauge {
    &__table-truncate {
      td {
        max-width: 10vw;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &__modal-style {
      * {
        color: var(--ds-color-snow) !important;
      }

      h3 {
        font-weight: var(--ds-font-weight-bold);
      }
    }

    &__v-dialog {
      overflow-x: hidden;
    }
  }
}
</style>

